/**
 * Longest edge a stored postcard photo may have.
 *
 * Postcards are ~1500px at any sane print resolution, so this is lossless in practice for
 * the actual subject while bounding what a modern phone camera would otherwise upload.
 * It matters operationally: the VPS has 25 GB of disk shared with six other sites, and the
 * photo backups mirror plus tarball roughly triples each file.
 */
const MAX_EDGE = 2000;

/**
 * iOS Safari will not back a canvas past a total pixel budget, and hands back a blank or
 * null result instead of throwing. Our own MAX_EDGE keeps us far below it; this is the
 * backstop for a source whose aspect ratio is extreme enough to slip past that.
 */
const MAX_CANVAS_PIXELS = 16_777_216;

/**
 * Nothing here may wait forever.
 *
 * Both the decode and the encode can stall on a phone — a huge photo, a format the
 * browser half-supports, a `toBlob` callback that never fires — and the upload used to
 * sit on "Removing private metadata…" with no way out. A slow phone still finishes well
 * inside this; a stuck one now surfaces as an error the owner can act on.
 */
const STEP_TIMEOUT_MS = 25_000;

function withTimeout<T>(work: Promise<T>, message: string) {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), STEP_TIMEOUT_MS);
    work.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

/**
 * Decodes through an <img> rather than createImageBitmap.
 *
 * createImageBitmap materialises the source at full resolution before we get a chance to
 * shrink it — a 48-megapixel phone photo is ~195 MB of bitmap — which is why this step
 * failed on phones and never on desktops. An <img> lets the browser manage decode memory
 * and downsample as it draws, and on iOS it also covers formats (HEIC) that
 * createImageBitmap declines.
 */
function decodeImage(url: string) {
  return withTimeout(new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("This photo is in a format the browser could not open."));
    image.src = url;
  }), "The photo took too long to open. A smaller photo will work.");
}

function encodeCanvas(canvas: HTMLCanvasElement) {
  return withTimeout(new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      // Safari can hand back null on a canvas it could not read; the synchronous encoder
      // usually still manages it.
      try {
        const encoded = canvas.toDataURL("image/jpeg", 0.94);
        const bytes = Uint8Array.from(atob(encoded.split(",")[1]), (character) => character.charCodeAt(0));
        resolve(new Blob([bytes], { type: "image/jpeg" }));
      } catch {
        reject(new Error("The postcard photo could not be cleaned."));
      }
    }, "image/jpeg", 0.94);
  }), "Cleaning the photo took too long. A smaller photo will work.");
}

/** Scales the source down to fit both MAX_EDGE and the canvas pixel budget. */
function targetSize(sourceWidth: number, sourceHeight: number) {
  const longestEdge = Math.max(sourceWidth, sourceHeight);
  const areaScale = Math.sqrt(MAX_CANVAS_PIXELS / (sourceWidth * sourceHeight));
  const scale = Math.min(1, MAX_EDGE / longestEdge, areaScale);
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

/**
 * Re-encodes a chosen photo, which is what drops its EXIF — including the GPS coordinates
 * of the house it was photographed in.
 *
 * The server strips metadata again on the way to disk, so a browser that cannot manage
 * this is a failed upload rather than a leaked location.
 */
export async function sanitizeImage(image: Blob, name = "postcard.jpg") {
  const url = URL.createObjectURL(image);
  let canvas: HTMLCanvasElement | null = null;
  try {
    const source = await decodeImage(url);
    // naturalWidth/Height are already EXIF-rotated, and so is the draw below.
    const { width, height } = targetSize(source.naturalWidth, source.naturalHeight);

    canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser could not prepare the postcard photo safely.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);

    const cleanBlob = await encodeCanvas(canvas);
    const cleanName = name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([cleanBlob], cleanName, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(url);
    // Zeroing the canvas releases its backing store immediately, which matters when the
    // next photo in a batch has to be decoded on the same phone.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
